import { CommonModule } from '@angular/common';
import { Component, computed, Input, signal } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Event } from '../../../../api/scheduler-service';
import { decorateEvents, SimulatedEvent, SimulatedExecution } from '../event-simulation.util';
import { SimulationParams } from '../simulation-options/simulation-options.component';

/**
 * Table-shaped counterpart to scheduler-events-gantt — same simulation pipeline,
 * different rendering. Operators sometimes want to read exact times and
 * applied-jitter values rather than eyeball bar widths; this gives them that.
 *
 * <p>Render shape: one row per execution, NOT per event. Events with multiple
 * executions (continuation mode) get N rows that share the same eventId so a
 * grouping/striping rule could be added later if visually warranted.
 *
 * <p>The component intentionally takes the raw {@code events} + {@code SimulationParams}
 * — the same inputs the Gantt accepts — so the parent can swap views without
 * rewiring inputs.
 */
@Component({
    selector: 'scheduler-events-table',
    standalone: true,
    imports: [CommonModule, MatTableModule, MatIconModule, MatTooltipModule],
    templateUrl: './scheduler-events-table.component.html',
    styleUrl: './scheduler-events-table.component.css',
})
export class SchedulerEventsTableComponent {

    private _events = signal<Event[]>([]);
    private _params = signal<SimulationParams>({ durationMs: 0, jitterPercent: 0, failurePercent: 0, driftMs: 0, sampleSize: 10 });

    @Input()
    set events(v: Event[]) { this._events.set(v ?? []); }

    @Input()
    set simulationParams(v: SimulationParams | undefined) {
        if (v) this._params.set(v);
    }

    /** Flat row stream — one row per execution. Memoised by signal/computed
     *  so re-renders don't re-walk the events list when nothing changed. */
    readonly rows = computed<Row[]>(() => {
        const decorated: SimulatedEvent[] = decorateEvents(this._events(), this._params());
        const out: Row[] = [];
        for (const ev of decorated) {
            // Multi-execution events: number them within the event so the
            // operator can tell continuation runs apart from event boundaries.
            ev.executions.forEach((ex, indexWithinEvent) => {
                out.push({
                    eventId: ev.event.eventId,
                    originalFireTime: ev.originalFireTime,
                    executionIndexWithinEvent: indexWithinEvent + 1,
                    executionsInEvent: ev.executions.length,
                    flatIndex: ex.flatIndex,
                    sim: ex,
                });
            });
        }
        return out;
    });

    readonly displayedColumns = [
        'index',
        'eventId',
        'originalFireTime',
        'simulatedStart',
        'simulatedEnd',
        'durationMs',
        'driftAppliedMs',
        'jitterAppliedMs',
        'status',
    ];

    /** Format ms as "1m 12s 480ms" up to a few units — readable for ops. */
    formatMs(ms: number | undefined): string {
        if (!ms || ms <= 0) return '—';
        const sign = ms < 0 ? '-' : '';
        let n = Math.abs(ms);
        const h = Math.floor(n / 3_600_000); n -= h * 3_600_000;
        const m = Math.floor(n / 60_000);    n -= m * 60_000;
        const s = Math.floor(n / 1_000);     n -= s * 1_000;
        const parts: string[] = [];
        if (h) parts.push(`${h}h`);
        if (m) parts.push(`${m}m`);
        if (s) parts.push(`${s}s`);
        if (n && parts.length < 2) parts.push(`${n}ms`);
        return sign + (parts.join(' ') || '0ms');
    }

    formatSignedMs(ms: number | undefined): string {
        if (ms === undefined || ms === null) return '—';
        if (ms === 0) return '0';
        return (ms > 0 ? '+' : '') + this.formatMs(ms);
    }

    /** Trim long uuid-shaped event ids to "abcd…1234" so the column doesn't dominate
     *  the row. Full id appears in the matTooltip on hover. */
    shortId(id?: string): string {
        if (!id) return '—';
        if (id.length <= 12) return id;
        return id.slice(0, 6) + '…' + id.slice(-4);
    }
}

interface Row {
    eventId?: string;
    originalFireTime?: Date;
    /** 1-based position of this execution within its event (e.g. "2 of 3"). */
    executionIndexWithinEvent: number;
    executionsInEvent: number;
    /** 0-based position across the whole flattened list — same as the Gantt's index. */
    flatIndex: number;
    sim: SimulatedExecution;
}
